import React, { useState, useEffect } from 'react';
import client from '../api/client';
import { C, Btn, Toast, useNotify } from '../utils/styles';
import { Skeleton } from '../components/Layout';
import razorpayLogo  from '../assets/integrations/RazorPay.webp';
import paypalLogo    from '../assets/integrations/PayPal.webp';
import stripeLogo    from '../assets/integrations/Stripe.webp';
import claudeLogo    from '../assets/integrations/Claude.webp';
import openrouterLogo from '../assets/integrations/OpenRouter.webp';
import whatsappLogo  from '../assets/integrations/WhatsApp.webp';



// ── Provider catalogue ────────────────────────────────────────
const PROVIDERS = [
  // ── Payments ──
  {
    id:'razorpay', label:'Razorpay', category:'payments',
    logo:razorpayLogo,
    color:'#2d81f7', bg:'#eff6ff',
    desc:'Accept payments from Indian customers via UPI, cards, netbanking and wallets.',
    status:'available',
    fields:[
      { key:'key_id',     label:'Key ID',     type:'text',     placeholder:'rzp_live_xxxxxxxxxxxx' },
      { key:'key_secret', label:'Key Secret', type:'password', placeholder:'Enter your key secret' },
    ],
    docs:'https://razorpay.com/docs/payments/dashboard/account-access/id-key/',
    testable:true,
  },
  {
    id:'paypal', label:'PayPal', category:'payments',
    logo:paypalLogo,
    color:'#003087', bg:'#eff6ff',
    desc:'Accept international payments via PayPal from customers worldwide.',
    status:'available',
    fields:[
      { key:'client_id',     label:'Client ID',     type:'text',     placeholder:'AXxx...' },
      { key:'client_secret', label:'Client Secret', type:'password', placeholder:'Enter your client secret' },
      { key:'mode',          label:'Mode',          type:'select',   options:['sandbox','live'] },
    ],
    docs:'https://developer.paypal.com/api/rest/',
    testable:true,
  },
  {
    id:'stripe', label:'Stripe', category:'payments',
    logo:stripeLogo,
    color:'#635bff', bg:'#f5f4ff',
    desc:'Accept global payments with Stripe — cards, wallets and more.',
    status:'coming_soon',
    fields:[],
    docs:'https://stripe.com/docs/keys',
    testable:false,
  },

  // ── AI ──
  {
    id:'anthropic', label:'Anthropic Claude', category:'ai',
    logo:claudeLogo,
    color:'#d97706', bg:'#fffbeb',
    desc:'Power AI lead scoring, email drafts, pipeline insights and workflow suggestions.',
    status:'available',
    fields:[
      { key:'api_key', label:'API Key', type:'password', placeholder:'sk-ant-...' },
    ],
    docs:'https://console.anthropic.com/settings/keys',
    testable:false,
  },
  {
    id:'openai', label:'OpenAI', category:'ai',
    logo:'https://www.google.com/s2/favicons?sz=128&domain_url=openai.com',
    color:'#10a37f', bg:'#f0fdf4',
    desc:'Use GPT-4 and other OpenAI models as an alternative to Anthropic.',
    status:'coming_soon',
    fields:[
      { key:'api_key', label:'API Key', type:'password', placeholder:'sk-...' },
    ],
    docs:'https://platform.openai.com/api-keys',
    testable:false,
  },
  {
    id:'openrouter', label:'OpenRouter', category:'ai',
    logo:openrouterLogo,
    color:'#6366f1', bg:'#f5f3ff',
    desc:'Access hundreds of AI models (GPT-4, Claude, Llama, Mistral) through a single API.',
    status:'coming_soon',
    fields:[
      { key:'api_key', label:'API Key', type:'password', placeholder:'sk-or-...' },
    ],
    docs:'https://openrouter.ai/keys',
    testable:false,
  },
  {
    id:'gemini', label:'Google Gemini', category:'ai',
    logo:'https://www.google.com/s2/favicons?sz=128&domain_url=google.com',
    color:'#4285f4', bg:'#eff6ff',
    desc:'Use Google Gemini models for AI-powered CRM features.',
    status:'coming_soon',
    fields:[
      { key:'api_key', label:'API Key', type:'password', placeholder:'AIza...' },
    ],
    docs:'https://aistudio.google.com/app/apikey',
    testable:false,
  },

  // ── Ads & Leads ──
  {
    id:'meta_ads', label:'Meta Ads', category:'ads',
    logo:'https://www.google.com/s2/favicons?sz=128&domain_url=meta.com',
    color:'#0866ff', bg:'#eff6ff',
    desc:'Automatically import leads from Facebook and Instagram lead ads into the CRM.',
    status:'coming_soon',
    fields:[],
    docs:'https://developers.facebook.com/docs/marketing-api',
    testable:false,
  },
  {
    id:'google_ads', label:'Google Ads', category:'ads',
    logo:'https://www.google.com/s2/favicons?sz=128&domain_url=ads.google.com',
    color:'#ea4335', bg:'#fef2f2',
    desc:'Sync Google Ads leads and conversions directly into your CRM pipeline.',
    status:'coming_soon',
    fields:[],
    docs:'https://developers.google.com/google-ads/api/docs/start',
    testable:false,
  },
  {
    id:'whatsapp', label:'WhatsApp Business', category:'ads',
    logo:whatsappLogo,
    color:'#25d366', bg:'#f0fdf4',
    desc:'Send automated WhatsApp messages and capture leads from WhatsApp conversations.',
    status:'coming_soon',
    fields:[],
    docs:'https://developers.facebook.com/docs/whatsapp',
    testable:false,
  },
];

const CATEGORIES = [
  { id:'all',      label:'All Integrations', icon:'ti-grid-dots'      },
  { id:'payments', label:'Payments',         icon:'ti-credit-card'    },
  { id:'ai',       label:'AI & ML',          icon:'ti-sparkles'       },
  { id:'ads',      label:'Ads & Lead Gen',   icon:'ti-ad-2'           },
];

// ── Connect modal ─────────────────────────────────────────────
const ConnectModal = ({ provider, integration, onClose, onSave, onDelete, onTest }) => {
  const [form,       setForm]       = useState({});
  const [showPass,   setShowPass]   = useState({});
  const [saving,     setSaving]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);

  const configured = integration?.configured === true;

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(provider.id, form); onClose(); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const result = await onTest(provider.id);
      setTestResult({ success:true, message:result.message });
    } catch (err) {
      setTestResult({ success:false, message:err.response?.data?.error||'Test failed' });
    } finally { setTesting(false); }
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:16, width:480, maxWidth:'100%', padding:28, boxShadow:'0 25px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20 }}>
          <div style={{ width:52, height:52, borderRadius:12, background:provider.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
            <img src={provider.logo} alt={provider.label} style={{ width:36, height:36, objectFit:'contain' }}
              onError={e => { e.target.style.display='none'; e.target.parentNode.innerHTML=`<span style="font-size:20px;font-weight:700;color:${provider.color}">${provider.label[0]}</span>`; }} />
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:16, color:C.dark }}>{provider.label}</div>
            <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{provider.desc}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, fontSize:20 }}><i className="ti ti-x" /></button>
        </div>

        {/* Already configured banner */}
        {configured && (
          <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'10px 14px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
            <i className="ti ti-circle-check" style={{ color:'#16a34a', fontSize:18 }} />
            <div style={{ flex:1, fontSize:13, color:'#166534' }}>Connected · last updated {integration.updated_at ? new Date(integration.updated_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '—'}</div>
          </div>
        )}

        {/* Masked existing config */}
        {configured && integration?.config && (
          <div style={{ background:'#f9fafb', borderRadius:8, padding:'10px 12px', marginBottom:16, border:`1px solid ${C.border}` }}>
            {Object.entries(integration.config).map(([k,v]) => (
              <div key={k} style={{ display:'flex', gap:8, fontSize:11, marginBottom:2 }}>
                <span style={{ color:C.muted, minWidth:90 }}>{k}:</span>
                <code style={{ color:C.text }}>{v}</code>
              </div>
            ))}
          </div>
        )}

        {/* Fields */}
        {provider.fields.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:10 }}>
              {configured ? 'Update credentials' : 'Enter credentials'}
            </div>
            <div style={{ display:'grid', gap:10 }}>
              {provider.fields.map(field => (
                <div key={field.key}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                    <label style={{ fontSize:12, fontWeight:500, color:C.text }}>{field.label}</label>
                    {field.type==='password' && (
                      <button type="button" onClick={() => setShowPass(p=>({...p,[field.key]:!p[field.key]}))}
                        style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:C.primary }}>
                        {showPass[field.key]?'Hide':'Show'}
                      </button>
                    )}
                  </div>
                  {field.type==='select' ? (
                    <select value={form[field.key]||''} onChange={e=>setForm(f=>({...f,[field.key]:e.target.value}))}
                      style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', boxSizing:'border-box' }}>
                      <option value="">Select...</option>
                      {field.options.map(o=><option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={field.type==='password'?(showPass[field.key]?'text':'password'):'text'}
                      placeholder={field.placeholder} value={form[field.key]||''}
                      onChange={e=>setForm(f=>({...f,[field.key]:e.target.value}))}
                      style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:field.type==='password'?'monospace':undefined }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Docs link */}
        <div style={{ fontSize:11, color:C.faint, marginBottom:16 }}>
          <i className="ti ti-book" style={{ marginRight:4 }} />
          <a href={provider.docs} target="_blank" rel="noreferrer" style={{ color:C.primary }}>
            View {provider.label} documentation <i className="ti ti-external-link" style={{ fontSize:10 }} />
          </a>
        </div>

        {/* Test result */}
        {testResult && (
          <div style={{ background:testResult.success?'#f0fdf4':'#fef2f2', border:`1px solid ${testResult.success?'#bbf7d0':'#fecaca'}`, borderRadius:8, padding:'8px 12px', marginBottom:14, fontSize:12, color:testResult.success?'#166534':'#991b1b' }}>
            {testResult.message}
          </div>
        )}

        {/* Actions */}
        <div style={{ display:'flex', gap:8 }}>
          {provider.fields.length > 0 && (
            <Btn onClick={handleSave} disabled={saving || provider.fields.filter(f=>f.type!=='select').every(f=>!form[f.key])}>
              <i className={`ti ${saving?'ti-loader ti-spin':'ti-plug'}`} /> {saving?'Connecting...':configured?'Update':'Connect'}
            </Btn>
          )}
          {configured && provider.testable && (
            <Btn variant="outline" onClick={handleTest} disabled={testing}>
              <i className={`ti ${testing?'ti-loader ti-spin':'ti-plug-connected'}`} /> {testing?'Testing...':'Test Connection'}
            </Btn>
          )}
          {configured && (
            <button onClick={() => { onDelete(provider.id); onClose(); }}
              style={{ padding:'8px 14px', borderRadius:8, border:'none', background:'#fef2f2', color:C.danger, cursor:'pointer', fontSize:13, fontWeight:500, marginLeft:'auto' }}>
              <i className="ti ti-trash" /> Disconnect
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Integration card ──────────────────────────────────────────
const IntegrationCard = ({ provider, integration, onConnect }) => {
  const configured   = integration?.configured === true;
  const isActive     = integration?.is_active === true;
  const comingSoon   = provider.status === 'coming_soon';

  return (
    <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${configured ? provider.color+'44' : C.border}`, padding:20, display:'flex', flexDirection:'column', gap:14, cursor:comingSoon?'default':'pointer', transition:'box-shadow 0.15s', position:'relative', overflow:'hidden' }}
      onMouseEnter={e => { if(!comingSoon) e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; }}>

      {/* Coming soon ribbon */}
      {comingSoon && (
        <div style={{ position:'absolute', top:12, right:-20, background:'#f3f4f6', color:'#6b7280', fontSize:9, fontWeight:700, padding:'3px 24px', transform:'rotate(30deg)', letterSpacing:0.5 }}>
          SOON
        </div>
      )}

      {/* Logo + status */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div style={{ width:48, height:48, borderRadius:10, background:provider.bg, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
          <img src={provider.logo} alt={provider.label} style={{ width:28, height:28, objectFit:'contain', imageRendering:'crisp-edges' }}
            onError={e => {
              e.target.style.display='none';
              e.target.parentNode.innerHTML=`<span style="font-size:18px;font-weight:700;color:${provider.color}">${provider.label[0]}</span>`;
            }} />
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
          {configured && isActive && (
            <span style={{ background:'#f0fdf4', color:'#16a34a', border:'1px solid #bbf7d0', borderRadius:20, padding:'2px 9px', fontSize:10, fontWeight:600 }}>✓ Connected</span>
          )}
          {configured && !isActive && (
            <span style={{ background:'#fef3c7', color:'#92400e', borderRadius:20, padding:'2px 9px', fontSize:10, fontWeight:600 }}>Disabled</span>
          )}
          {comingSoon && (
            <span style={{ background:'#f3f4f6', color:'#6b7280', borderRadius:20, padding:'2px 9px', fontSize:10, fontWeight:600 }}>Coming Soon</span>
          )}
        </div>
      </div>

      {/* Name + desc */}
      <div>
        <div style={{ fontWeight:700, fontSize:14, color:C.dark, marginBottom:4 }}>{provider.label}</div>
        <div style={{ fontSize:12, color:C.muted, lineHeight:1.5 }}>{provider.desc}</div>
      </div>

      {/* Connect button */}
      <button
        onClick={() => !comingSoon && onConnect(provider)}
        disabled={comingSoon}
        style={{ padding:'8px 14px', borderRadius:8, border:`1px solid ${comingSoon?C.border:provider.color+'44'}`, background:comingSoon?'#f9fafb':configured?`${provider.color}0d`:'#fff', color:comingSoon?C.faint:provider.color, cursor:comingSoon?'not-allowed':'pointer', fontSize:12, fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', gap:6, transition:'all 0.15s', marginTop:'auto' }}>
        <i className={`ti ${comingSoon?'ti-clock':configured?'ti-settings':'ti-plug'}`} style={{ fontSize:14 }} />
        {comingSoon ? 'Coming Soon' : configured ? 'Manage' : 'Connect'}
      </button>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────
export default function Integrations() {
  const [integrations, setIntegrations] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [category,     setCategory]     = useState('all');
  const [search,       setSearch]       = useState('');
  const [activeModal,  setActiveModal]  = useState(null);
  const [notification, notify]          = useNotify();

  useEffect(() => { fetchIntegrations(); }, []); // eslint-disable-line

  const fetchIntegrations = async () => {
    setLoading(true);
    try { const { data } = await client.get('/api/integrations'); setIntegrations(data||[]); }
    catch { notify('Failed to load integrations', 'error'); }
    finally { setLoading(false); }
  };

  const getIntegration = (providerId) => {
    const row = integrations.find(i => i.provider === providerId);
    return row ? { configured:true, ...row } : { configured:false };
  };

  const handleSave = async (providerId, config) => {
    try {
      await client.post(`/api/integrations/${providerId}`, { config });
      notify(`${PROVIDERS.find(p=>p.id===providerId)?.label} connected!`);
      fetchIntegrations();
    } catch (err) { notify(err.response?.data?.error||'Failed to save','error'); throw err; }
  };

  const handleDelete = async (providerId) => {
    if (!window.confirm('Disconnect this integration?')) return;
    try { await client.delete(`/api/integrations/${providerId}`); notify('Disconnected'); fetchIntegrations(); }
    catch { notify('Failed','error'); }
  };

  const handleTest = async (providerId) => {
    const { data } = await client.post(`/api/integrations/${providerId}/test`);
    return data;
  };

  const handleToggle = async (providerId) => {
    try {
      const { data } = await client.patch(`/api/integrations/${providerId}/toggle`);
      notify(`Integration ${data.is_active?'enabled':'disabled'}`);
      fetchIntegrations();
    } catch { notify('Failed','error'); }
  };

  const connectedCount = integrations.filter(i => i.is_active).length;

  const filtered = PROVIDERS.filter(p => {
    const matchCat    = category === 'all' || p.category === category;
    const matchSearch = !search || p.label.toLowerCase().includes(search.toLowerCase()) || p.desc.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div style={{ display:'flex', gap:24, minHeight:'calc(100vh - 100px)' }}>
      <Toast {...notification} />

      {/* ── Left sidebar ── */}
      <div style={{ width:220, flexShrink:0 }}>
        <div style={{ fontWeight:700, fontSize:15, color:C.dark, marginBottom:16 }}>Integrations</div>
        <nav style={{ display:'flex', flexDirection:'column', gap:2 }}>
          {CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => setCategory(cat.id)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderRadius:8, border:'none', background:category===cat.id?'var(--color-primary-light)':'transparent', color:category===cat.id?C.primary:C.text, fontSize:13, fontWeight:category===cat.id?600:400, cursor:'pointer', textAlign:'left' }}>
              <i className={`ti ${cat.icon}`} style={{ fontSize:16, color:category===cat.id?C.primary:C.muted }} />
              {cat.label}
              <span style={{ marginLeft:'auto', background:category===cat.id?C.primary:'#f3f4f6', color:category===cat.id?'#fff':C.muted, borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:600 }}>
                {cat.id==='all' ? PROVIDERS.length : PROVIDERS.filter(p=>p.category===cat.id).length}
              </span>
            </button>
          ))}
        </nav>

        {connectedCount > 0 && (
          <div style={{ marginTop:20, padding:'10px 12px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#166534', marginBottom:2 }}>Active</div>
            <div style={{ fontSize:20, fontWeight:700, color:'#16a34a' }}>{connectedCount}</div>
            <div style={{ fontSize:11, color:'#16a34a' }}>integration{connectedCount!==1?'s':''} connected</div>
          </div>
        )}
      </div>

      {/* ── Main content ── */}
      <div style={{ flex:1 }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
          <div>
            <h1 style={{ fontSize:18, fontWeight:700, color:C.dark, marginBottom:2 }}>
              {CATEGORIES.find(c=>c.id===category)?.label || 'All Integrations'}
            </h1>
            <p style={{ fontSize:12, color:C.muted }}>Connect your tools to supercharge your CRM</p>
          </div>
          <div style={{ position:'relative' }}>
            <i className="ti ti-search" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.muted, fontSize:14 }} />
            <input placeholder="Search integrations..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{ padding:'8px 12px 8px 32px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', width:220 }} />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
            {Array(6).fill(null).map((_,i) => (
              <div key={i} style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:20 }}>
                <Skeleton circle size={48} style={{ marginBottom:14 }} />
                <Skeleton height={14} width="50%" style={{ marginBottom:8 }} />
                <Skeleton height={11} width="90%" style={{ marginBottom:4 }} />
                <Skeleton height={11} width="70%" style={{ marginBottom:16 }} />
                <Skeleton height={34} radius={8} />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:48, color:C.faint }}>
            <i className="ti ti-search" style={{ fontSize:36, display:'block', marginBottom:12 }} />
            No integrations found for "{search}"
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
            {filtered.map(provider => (
              <IntegrationCard
                key={provider.id}
                provider={provider}
                integration={getIntegration(provider.id)}
                onConnect={setActiveModal}
              />
            ))}
          </div>
        )}

        {/* Info note */}
        <div style={{ marginTop:20, background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:10, padding:'10px 14px', fontSize:12, color:'#0369a1' }}>
          <i className="ti ti-info-circle" style={{ marginRight:6 }} />
          <strong>White-label agencies:</strong> Add your own Razorpay and PayPal credentials to collect subscription payments from your clients directly. Sub-accounts automatically inherit your payment configuration.
        </div>
      </div>

      {/* ── Connect modal ── */}
      {activeModal && (
        <ConnectModal
          provider={activeModal}
          integration={getIntegration(activeModal.id)}
          onClose={() => setActiveModal(null)}
          onSave={handleSave}
          onDelete={handleDelete}
          onTest={handleTest}
          onToggle={handleToggle}
        />
      )}
    </div>
  );
}
